'use client';

import { Map, ArrowRight } from 'lucide-react';
import { trackCtaClick } from '@/lib/analytics';

/**
 * Planet Roadmap product CTA — funnels InventiveHQ traffic to Planet Roadmap
 * (planetroadmap.com), Sean's roadmap / sprint / product-management SaaS.
 *
 * Targets agile / project-management content: readers learning about agile,
 * sprints, or roadmapping who are one step away from needing a tool for it.
 *
 * Bundle-safe: uses a plain <a> tag (NOT next/link) so it can be imported into
 * R2-bundled tools as well. See CLAUDE.md "R2 Tool Bundle System".
 *
 * Destination + UTM are centralized in buildPlanetRoadmapUrl() — the single
 * swap point if we later re-route to a different landing/signup URL.
 */

const PLANETROADMAP_BASE = 'https://planetroadmap.com';

export type PlanetRoadmapVariant = 'agile' | 'jira-switch';

/** Message-matched landing page per variant. */
const LANDING_PATH: Record<PlanetRoadmapVariant, string> = {
  agile: '/features',
  'jira-switch': '/compare/jira',
};

const CAMPAIGN: Record<PlanetRoadmapVariant, string> = {
  agile: 'agile-pm',
  'jira-switch': 'jira-alternative',
};

interface PlanetRoadmapCTAProps {
  /** Which angle to pitch (controls copy, landing page + utm_campaign). */
  variant?: PlanetRoadmapVariant;
  /** Source page slug, for utm_content + click tracking. */
  source: string;
  /** Tighter spacing when embedded in a sidebar card. */
  compact?: boolean;
}

/** Centralized destination builder — swap this to re-route all Planet Roadmap CTAs. */
export function buildPlanetRoadmapUrl(variant: PlanetRoadmapVariant, source: string): string {
  const params = new URLSearchParams({
    utm_source: 'inventivehq',
    utm_medium: 'blog_cta',
    utm_campaign: CAMPAIGN[variant],
    utm_content: source,
  });
  return `${PLANETROADMAP_BASE}${LANDING_PATH[variant]}?${params.toString()}`;
}

function copyFor(variant: PlanetRoadmapVariant) {
  switch (variant) {
    case 'agile':
      return {
        eyebrow: 'Put it into practice',
        title: 'Plan sprints and roadmaps without the overhead',
        body:
          'Planet Roadmap turns agile theory into a working process — sprints, roadmaps, and feature requests in one lightweight tool your team will actually use.',
        button: 'Try Planet Roadmap free',
      };
    case 'jira-switch':
      return {
        eyebrow: 'Tired of fighting Jira?',
        title: 'A simpler way to plan sprints and roadmaps',
        body:
          'Planet Roadmap gives you sprints, roadmaps, and feature-request tracking without the configuration maze. See how it compares to Jira.',
        button: 'Compare with Jira',
      };
  }
}

export default function PlanetRoadmapCTA({ variant = 'agile', source, compact }: PlanetRoadmapCTAProps) {
  const c = copyFor(variant);
  const href = buildPlanetRoadmapUrl(variant, source);

  return (
    <div
      className={`${compact ? 'p-4' : 'p-5'} rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50 to-sky-50`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <Map className="w-5 h-5 text-indigo-600" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600 mb-1">
            {c.eyebrow}
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-1">{c.title}</h3>
          <p className="text-sm text-gray-600 mb-3">{c.body}</p>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCtaClick(`PlanetRoadmap: ${variant}`, 'blog_cta', href)}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white px-4 py-2 rounded-md font-medium text-sm hover:shadow-lg transition-all"
          >
            {c.button}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
